import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { runTask } from "@/lib/tasks";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, Copy, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const LABELS = [
  { key: "control", label: "Control", note: "Your default hook, tightened." },
  { key: "contrarian", label: "Contrarian", note: "Take a stand against the accepted belief." },
  { key: "curiosity", label: "Curiosity", note: "Open a loop they must close by watching." },
  { key: "pain", label: "Pain", note: "Name the exact pain the ICP is feeling right now." },
];

export default function ScriptVariantsPage() {
  const { postId } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const topic = params.get("topic") || "";
  const hook = params.get("hook") || "";
  const script = params.get("script") || "";

  const [variants, setVariants] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/api/scripts/variants/${postId}`).then(({ data }) => setVariants(data));
  }, [postId]);

  const generate = async () => {
    setBusy(true);
    toast.message("Writing 4 hook variants…");
    try {
      const task = await runTask("/api/scripts/variants", {
        post_id: postId, topic, current_hook: hook, script,
      }, { intervalMs: 2000, maxMs: 180000 });
      setVariants(task.result);
      toast.success("Variants ready");
    } catch (e) { toast.error(e.response?.data?.detail || e.message || "Failed"); }
    finally { setBusy(false); }
  };

  const copy = async (t, label) => {
    await navigator.clipboard.writeText(t);
    toast.success(`${label} copied`);
  };

  return (
    <div className="min-h-screen">
      <div className="border-b border-border">
        <div className="px-8 py-8 flex items-center justify-between">
          <div>
            <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="btn-variants-back"><ArrowLeft className="h-4 w-4"/> Back</Button>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mt-3">Creative Director · Hook variants</div>
            <h1 className="mt-1 font-display font-black text-3xl tracking-tighter">{topic || "Post"}</h1>
          </div>
          <Button data-testid="btn-generate-variants" onClick={generate} disabled={busy}>
            {busy ? <><Loader2 className="h-4 w-4 animate-spin"/> Writing…</> : <><Sparkles className="h-4 w-4"/> {variants ? "Regenerate" : "Generate variants"}</>}
          </Button>
        </div>
      </div>

      <div className="p-8">
        <div className="border border-border rounded p-5 bg-card mb-6">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Current hook (control)</div>
          <div className="mt-1 font-display text-lg">“{hook}”</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="variants-grid">
          {LABELS.map((v) => (
            <div key={v.key} className="border border-border rounded p-5 bg-card" data-testid={`variant-${v.key}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono text-xs uppercase tracking-widest text-primary">{v.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{v.note}</div>
                </div>
                {variants?.[v.key] && (
                  <Button size="sm" variant="ghost" onClick={() => copy(variants[v.key], v.label)} data-testid={`btn-copy-${v.key}`}>
                    <Copy className="h-3 w-3"/> Copy
                  </Button>
                )}
              </div>
              <div className="mt-4 min-h-[96px] font-display text-xl font-bold leading-tight">
                {variants?.[v.key] ? `“${variants[v.key]}”` : <span className="text-muted-foreground font-normal font-sans text-sm">Not generated yet.</span>}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 border border-border rounded p-6 bg-secondary/50">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">More / Better / New</div>
          <div className="mt-2 text-sm leading-relaxed max-w-3xl">
            Film all 4 variants back-to-back — same body, different hooks. Post them across a week and let the winner become the seed of next week's <span className="font-semibold text-foreground">MORE</span> and <span className="font-semibold text-foreground">BETTER</span> experiments.
          </div>
        </div>
      </div>
    </div>
  );
}
